/**
 * The Swiss pairing engine for the weekly tournament ประลองยุทธภพ (docs/design/ideas/2026-10-11-weekly-tournament.md).
 * Pure functions over plain data: no World, no DOM, no timers; every draw comes from the injected `Rng`, so the same
 * entrants and the same results always give the same rounds. The online tournament reuses it as it is.
 *
 * - Rounds: `ceil(log2 n)`, at least 3 and at most 7.
 * - Each round pairs players of equal score (floating down to the nearest score when a group is odd), never a rematch,
 *   and in the first rounds avoids two of one guild.
 * - An odd field gives the bye to the lowest score that has not had one; a bye counts as a win.
 * - Standings: score, then Buchholz (the sum of the opponents' scores), then the result between the tied two
 *   (head-to-head), then the scores of the beaten opponents (Sonneborn-Berger), then the seed (the ladder rating), then
 *   the id (so the order is always total). `tied` flags a place only the seed or the id had to part.
 * - Top cut: a single-elimination bracket seeded 1 v n, 2 v n-1 …
 */
import type { Rng } from './rng';

export interface SwissEntrant {
  readonly id: number;
  /** The seed strength (the ladder rating): orders round one and breaks the last ties. */
  readonly rating: number;
  /** The guild (or any group) kept apart in the first rounds; absent: none. */
  readonly guild?: string | undefined;
}

export interface SwissPlayer extends SwissEntrant {
  score: number;
  /** Opponents met, in order (a bye is not in here). */
  opponents: number[];
  /** Beaten opponents (for head-to-head). */
  beat: number[];
  byes: number;
  /** Rounds played, wins, losses. */
  played: number;
}

export interface SwissState {
  readonly rounds: number;
  /** Rounds in which two of one guild are kept apart when it can be done. */
  readonly guildRounds: number;
  /** Rounds already paired (finished or under way). */
  round: number;
  players: SwissPlayer[];
}

export interface SwissPair {
  readonly a: number;
  /** null: a bye. */
  readonly b: number | null;
}

export const SWISS = { minRounds: 3, maxRounds: 7, guildRounds: 2, byeScore: 1, winScore: 1 } as const;

export const swissRounds = (n: number): number =>
  Math.min(SWISS.maxRounds, Math.max(SWISS.minRounds, Math.ceil(Math.log2(Math.max(2, n)))));

export function newSwiss(entrants: readonly SwissEntrant[], rounds = swissRounds(entrants.length)): SwissState {
  return {
    rounds,
    guildRounds: SWISS.guildRounds,
    round: 0,
    players: entrants.map((e) => ({ ...e, score: 0, opponents: [], beat: [], byes: 0, played: 0 })),
  };
}

const byId = (s: SwissState, id: number): SwissPlayer => {
  const p = s.players.find((x) => x.id === id);
  if (!p) throw new Error(`swiss: no player ${id}`);
  return p;
};

/** Player order before pairing: score high to low, then seed, with equal ones shuffled by the `rng`. */
function ordered(s: SwissState, rng: Rng): SwissPlayer[] {
  const tag = new Map(s.players.map((p) => [p.id, rng()] as const));
  return [...s.players].sort(
    (a, b) => b.score - a.score || b.rating - a.rating || tag.get(a.id)! - tag.get(b.id)!,
  );
}

/**
 * The next round's pairs. `s.round` goes up by one; pairs are not scored until `report`. Byes are scored at once.
 * Returns an empty list when every round has been paired.
 */
export function pairRound(s: SwissState, rng: Rng): SwissPair[] {
  if (s.round >= s.rounds) return [];
  const nextRound = s.round + 1;
  const keepGuilds = nextRound <= s.guildRounds;
  let pool = ordered(s, rng);
  const pairs: SwissPair[] = [];
  // The bye: the lowest score that has not had one (among those, the lowest seed).
  if (pool.length % 2 === 1) {
    const cand = [...pool].reverse().find((p) => p.byes === 0) ?? pool[pool.length - 1]!;
    pool = pool.filter((p) => p !== cand);
    cand.byes++;
    cand.score += SWISS.byeScore;
    pairs.push({ a: cand.id, b: null });
  }
  const met = (a: SwissPlayer, b: SwissPlayer): boolean => a.opponents.includes(b.id);
  const sameGuild = (a: SwissPlayer, b: SwissPlayer): boolean => !!a.guild && a.guild === b.guild;

  const solve = (strict: boolean): [SwissPlayer, SwissPlayer][] | null => {
    const used = new Set<number>();
    const out: [SwissPlayer, SwissPlayer][] = [];
    let nodes = 0;
    const dfs = (): boolean => {
      if (++nodes > 60000) return false;
      const p = pool.find((x) => !used.has(x.id));
      if (!p) return true;
      const ix = pool.indexOf(p);
      const group = pool.filter((x) => x.score === p.score).length;
      const half = Math.floor(group / 2);
      const cands = pool
        .slice(ix + 1)
        .filter((x) => !used.has(x.id) && !met(p, x) && !(strict && keepGuilds && sameGuild(p, x)))
        .sort((x, y) => {
          const dx = Math.abs(p.score - x.score);
          const dy = Math.abs(p.score - y.score);
          if (dx !== dy) return dx - dy;
          return Math.abs(pool.indexOf(x) - ix - half) - Math.abs(pool.indexOf(y) - ix - half);
        });
      used.add(p.id);
      for (const c of cands) {
        used.add(c.id);
        out.push([p, c]);
        if (dfs()) return true;
        out.pop();
        used.delete(c.id);
      }
      used.delete(p.id);
      return false;
    };
    return dfs() ? out : null;
  };

  let found = solve(true) ?? solve(false);
  if (!found) {
    // No rematch-free pairing exists (a tiny field in its last rounds): allow rematches, nearest scores first.
    const rest = [...pool];
    found = [];
    while (rest.length > 1) {
      const p = rest.shift()!;
      rest.sort((x, y) => Math.abs(p.score - x.score) - Math.abs(p.score - y.score));
      found.push([p, rest.shift()!]);
    }
  }
  for (const [x, y] of found) pairs.push({ a: x.id, b: y.id });
  s.round = nextRound;
  return pairs;
}

/** A finished bout: `winner` (one of the two) beat the other. */
export function reportResult(s: SwissState, a: number, b: number, winner: number): void {
  const pa = byId(s, a);
  const pb = byId(s, b);
  if (winner !== a && winner !== b) throw new Error('swiss: the winner is not one of the pair');
  pa.opponents.push(b);
  pb.opponents.push(a);
  pa.played++;
  pb.played++;
  const w = winner === a ? pa : pb;
  const l = winner === a ? pb : pa;
  w.score += SWISS.winScore;
  w.beat.push(l.id);
}

export interface SwissRow {
  readonly id: number;
  readonly rank: number;
  readonly score: number;
  readonly buchholz: number;
  /** Still level with the one above after score, Buchholz and head-to-head (only the seed parted them). */
  readonly tied: boolean;
}

/** Standings: score, Buchholz, head-to-head, seed, id. */
export function standings(s: SwissState): SwissRow[] {
  const bh = new Map(s.players.map((p) => [p.id, p.opponents.reduce((t, o) => t + byId(s, o).score, 0)] as const));
  const sb = new Map(s.players.map((p) => [p.id, p.beat.reduce((t, o) => t + byId(s, o).score, 0)] as const));
  const h2h = (a: SwissPlayer, b: SwissPlayer): number => (a.beat.includes(b.id) ? -1 : b.beat.includes(a.id) ? 1 : 0);
  const rows = [...s.players].sort(
    (a, b) =>
      b.score - a.score ||
      bh.get(b.id)! - bh.get(a.id)! ||
      h2h(a, b) ||
      sb.get(b.id)! - sb.get(a.id)! ||
      b.rating - a.rating ||
      a.id - b.id,
  );
  return rows.map((p, i) => {
    const prev = rows[i - 1];
    const level =
      !!prev &&
      prev.score === p.score &&
      bh.get(prev.id) === bh.get(p.id) &&
      h2h(prev, p) === 0 &&
      sb.get(prev.id) === sb.get(p.id);
    return { id: p.id, rank: i + 1, score: p.score, buchholz: bh.get(p.id)!, tied: level };
  });
}

/** The ids of the top `n`, best first. */
export const topCut = (s: SwissState, n: number): number[] =>
  standings(s)
    .slice(0, n)
    .map((r) => r.id);

/** Seed numbers (1-based) in bracket order for a power-of-two field: adjacent pairs meet first, and 1 meets 2 last. */
function bracketOrder(n: number): number[] {
  let o = [1];
  while (o.length < n) {
    const m = o.length * 2 + 1;
    o = o.flatMap((x) => [x, m - x]);
  }
  return o;
}

/** The first bracket round for ids in seed order (best first; n a power of two): 1 v n, 4 v n-3 … in bracket order. */
export function bracketPairs(seeded: readonly number[]): [number, number][] {
  const o = bracketOrder(seeded.length);
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < o.length; i += 2) out.push([seeded[o[i]! - 1]!, seeded[o[i + 1]! - 1]!]);
  return out;
}

/** The next bracket round from the winners of the last, in bracket order (adjacent winners meet). */
export function advanceBracket(winners: readonly number[]): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < winners.length; i += 2) out.push([winners[i]!, winners[i + 1]!]);
  return out;
}

/** True when everyone has a final rank (the Swiss is over). */
export const swissDone = (s: SwissState): boolean => s.round >= s.rounds;
